import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MagnifyingGlass, BookmarkSimple, Heart, BookOpen, DownloadSimple } from "@phosphor-icons/react";
import SortSelect, { LIST_SORT_OPTIONS } from "@/components/SortSelect";
import html2canvas from "html2canvas";
import { toast } from "sonner";

const CONFIG = {
  collection: { title: "Coleção", endpoint: "/me/collection", color: "#E63946", Icon: BookmarkSimple },
  wishlist: { title: "Desejos", endpoint: "/me/wishlist", color: "#7209B7", Icon: Heart },
  read: { title: "Já Lidos", endpoint: "/me/read", color: "#10B981", Icon: BookOpen },
};

export default function MyList() {
  const { type } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("added_recent");
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  const cfg = CONFIG[type];

  useEffect(() => {
    if (!loading && !user) { navigate("/login"); return; }
    if (!cfg) { navigate("/perfil"); return; }
    if (user) api.get(cfg.endpoint, { params: { sort } }).then(r => setItems(r.data));
  }, [user, loading, navigate, type, cfg, sort]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const lq = q.toLowerCase();
    return items.filter(m =>
      m.title?.toLowerCase().includes(lq) ||
      m.author?.toLowerCase().includes(lq) ||
      m.publisher?.toLowerCase().includes(lq) ||
      m.isbn?.toLowerCase().includes(lq) ||
      m.genres?.some(g => g.toLowerCase().includes(lq))
    );
  }, [items, q]);

  if (!cfg || !user) return null;
  const { title, color, Icon } = cfg;

  async function shareImage() {
    if (!exportRef.current) return;
    setExporting(true);
    try {
      await new Promise(r => setTimeout(r, 100));
      const canvas = await html2canvas(exportRef.current, { backgroundColor: "#0a0a0f", useCORS: true, scale: 2 });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `portugalmanga-${type}.png`;
      a.click();
      toast.success("Imagem gerada! Partilha nas tuas redes 🎉");
    } catch (e) {
      toast.error("Não foi possível gerar a imagem");
    } finally {
      setExporting(false);
    }
  }

  const exportCovers = items.slice(0, 12);

  return (
    <div className="pt-20 max-w-6xl mx-auto px-5 lg:px-8 pb-24" data-testid="my-list-page">
      <Link to="/perfil" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6">
        <ArrowLeft size={16} weight="bold" /> Voltar ao perfil
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <Icon size={26} weight="fill" style={{ color }} />
        <h1 className="font-display text-4xl font-extrabold tracking-tight">{title}</h1>
        <span className="ml-2 font-mono-accent text-sm text-zinc-500">{items.length} mangás</span>
        {items.length > 0 && (
          <Button onClick={shareImage} disabled={exporting} size="sm" variant="outline" className="ml-auto border-white/15 text-white hover:bg-white/5" data-testid="share-list-image-btn">
            <DownloadSimple size={16} weight="bold" className="mr-1.5" /> {exporting ? "A gerar..." : "Partilhar como imagem"}
          </Button>
        )}
      </div>

      <div className="my-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Pesquisar por título, autor, editora..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 bg-[#121217] border-white/10 h-11" data-testid="my-list-search" />
        </div>
        <SortSelect value={sort} onChange={setSort} options={LIST_SORT_OPTIONS} testid="my-list-sort" />
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center">
          <div className="font-display font-bold text-xl">{items.length === 0 ? `Sem mangás em "${title}"` : "Sem resultados"}</div>
          {items.length === 0 && <Link to="/database" className="inline-block mt-3 text-[#E63946] hover:underline">Explorar base de dados →</Link>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          {filtered.map(m => (
            <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="group" data-testid={`my-list-item-${m.manga_id}`}>
              <div className="aspect-[2/3] overflow-hidden rounded-xl bg-[#121217] border border-white/5">
                {m.cover_url ? <img src={m.cover_url} alt={m.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" /> : <div className="w-full h-full grid place-items-center text-zinc-700 text-xs">SEM CAPA</div>}
              </div>
              <div className="mt-3 font-display font-semibold text-sm line-clamp-2">{m.title}</div>
              {m.author && <div className="font-mono-accent text-[10px] text-zinc-500 mt-1">{m.author}</div>}
            </Link>
          ))}
        </div>
      )}

      {/* Off-screen export card for sharing */}
      <div style={{ position: "fixed", left: "-9999px", top: 0 }} aria-hidden="true">
        <div ref={exportRef} style={{ width: "1080px", padding: "56px", background: "linear-gradient(135deg,#1a0a1f 0%,#0a0a10 60%)", color: "#fff", fontFamily: "sans-serif" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "32px" }}>
            <div>
              <div style={{ color: color, fontSize: "16px", letterSpacing: "3px", textTransform: "uppercase", fontWeight: 700 }}>A minha lista · {title}</div>
              <div style={{ fontSize: "56px", fontWeight: 800, lineHeight: 1.05, marginTop: "6px" }}>{user.name || "A minha coleção"}</div>
              <div style={{ color: "#a1a1aa", fontSize: "22px", marginTop: "8px" }}>{items.length} mangás em PortugalManga</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "30px", fontWeight: 800 }}>PortugalManga</div>
              <div style={{ color: color, fontSize: "16px", letterSpacing: "2px" }}>portugalmanga.pt</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: "16px" }}>
            {exportCovers.map((m) => (
              <div key={m.manga_id} style={{ aspectRatio: "2/3", borderRadius: "10px", overflow: "hidden", background: "#121217", border: "1px solid rgba(255,255,255,0.08)" }}>
                {m.cover_url ? (
                  <img src={resolveImageUrl(m.cover_url)} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#52525b", fontSize: "12px" }}>SEM CAPA</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
